import { useRef, useCallback, useEffect } from 'react';

interface UseStreamingScrollOptions {
  enabled?: boolean;
  scrollThreshold?: number;
  updateInterval?: number;
}

export const useStreamingScroll = (options: UseStreamingScrollOptions = {}) => {
  const {
    enabled = true,
    scrollThreshold = 100,
    updateInterval = 8 // ~120fps
  } = options;

  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isStreamingRef = useRef(false);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollHeightRef = useRef(0);

  // Check if user is near bottom
  const isNearBottom = useCallback(() => {
    if (!scrollContainerRef.current) return true;
    
    const container = scrollContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    return (scrollHeight - scrollTop - clientHeight) <= scrollThreshold;
  }, [scrollThreshold]);

  // Start continuous scrolling during streaming
  const startStreamingScroll = useCallback(() => {
    if (!enabled || !scrollContainerRef.current || isStreamingRef.current) return;
    
    isStreamingRef.current = true;
    lastScrollHeightRef.current = scrollContainerRef.current.scrollHeight;

    // Clear any existing interval
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }

    // Start continuous scrolling
    scrollIntervalRef.current = setInterval(() => {
      if (!scrollContainerRef.current || !isStreamingRef.current) return;
      
      const currentScrollHeight = scrollContainerRef.current.scrollHeight;
      
      // Only scroll if content has grown
      if (currentScrollHeight > lastScrollHeightRef.current) {
        const nearBottom = isNearBottom();
        scrollContainerRef.current.scrollTo({ 
          top: currentScrollHeight, 
          behavior: nearBottom ? 'smooth' : 'auto' 
        });
        lastScrollHeightRef.current = currentScrollHeight;
      }
    }, updateInterval);
  }, [enabled, isNearBottom, updateInterval]);

  // Stop streaming scroll
  const stopStreamingScroll = useCallback(() => {
    isStreamingRef.current = false;
    
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Set scroll container
  const setScrollContainer = useCallback((element: HTMLElement | null) => {
    scrollContainerRef.current = element;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopStreamingScroll();
    };
  }, [stopStreamingScroll]);

  return {
    messagesEndRef,
    setScrollContainer,
    startStreamingScroll,
    stopStreamingScroll,
    isStreaming: isStreamingRef.current
  };
};
